import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ProjectsTable.scss';
import Project from '../../../models/Project';
import { SortParameterProject } from '../../../models/SortParameterProjectEnum';
import { ProjectsTableRow } from '../ProjectsTableRow/ProjectsTableRow';
import { isMobile } from '../../../services/booleanUtil';
import PaginationTable from '../../PortfolioDetail/PaginationTable/PaginationTable';
import { Pagination } from '@material-ui/lab';
import usePaginationSort from '../../../hooks/usePaginationSort';
import { ExcelDownloadButton } from '../../Util/ExcelDownloadButton/ExcelDownloadButton';
import { useDispatch, useSelector } from 'react-redux';
import SearchTabErrorMessage from '../../Util/Errors/SearchTabErrorMessage/SearchTabErrorMessage';
import FiltersErrorMessage from '../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';
import SearchInput from '../../Util/CustomInput/SearchInput/SearchInput';
import { headers, LIMIT_SELECTED_PROJECTS, selectedKeys } from './ProjectsTableConstants';
import { setReviewSelectedProject } from '../../../redux/actions/reviewProjectsSelectedRows';
import { ReportsDownloadButton } from '../../Util/ReportsDownloadButton/ReportsDownloadButton';
import { getDataFromDownloadExcel } from './ProjectsTableUtil';
import useDataNormalize from '../../../hooks/useDataNormalize';
import useFilters from '../../../hooks/useFilters';

interface ReceivedProps {
  projects: Project[];
  setInFollow: any;
  sortTable: any;
  isAnnual: any;
  selectable?: boolean;
  withComments?: boolean;
  reviewStyle?: boolean;
  canCreateAccordance?: boolean;
  fromReview?: boolean;
  revision?: any;
  fnPaginacion?: any;
  initialPage?: number;
  numberOfPages?: number;
  numberOfProjects?: number;
  searchValue?: string;
  itsFollowed?: boolean;
}

type Props = ReceivedProps;

const selectableThead = reviewStyle => {
  return reviewStyle ? 'theader-selectable' : 'theader-text uppercase-text';
};

const selectableI = reviewStyle => {
  return reviewStyle ? { marginLeft: 9 } : {};
};

const tBodyClassName = isMobile => {
  return isMobile ? 'row-responsive-text' : 'row-text';
};

const headersList = (isAnnual, reviewStyle, canCreateAccordance) => {
  const list = [
    { sortValue: { concept: SortParameterProject.Code }, label: 'code' },
    { sortValue: { concept: SortParameterProject.Operation }, label: 'operation' },
    { sortValue: { concept: SortParameterProject.Name }, label: 'name' },
    { sortValue: { concept: SortParameterProject.Division }, label: 'division' },
    {
      sortValue: { concept: SortParameterProject.PercentTemporaryAdvanceMade },
      label: 'temporary_advance',
    },
    {
      sortValue: { concept: SortParameterProject.LastDisbursementInDays },
      label: 'last_accomplished_disbursement',
      hideOnCanCreateAccordance: true,
    },
    {
      sortValue: {
        concept: isAnnual
          ? SortParameterProject.PercentRealizedAnnualDisbursement
          : SortParameterProject.PercentRealizedDisbursement,
      },
      label: 'amount_disbursed',
    },
    {
      sortValue: {
        concept: isAnnual
          ? SortParameterProject.PendingDisbursementAnnualAmount
          : SortParameterProject.PendingDisbursementAmount,
      },
      label: 'undisbursed',
      hideOnCanCreateAccordance: true,
    },
    {
      sortValue: { concept: SortParameterProject.PmrValue },
      label: 'performance',
    },
  ];

  return canCreateAccordance ? list.filter(item => !item.hideOnCanCreateAccordance) : list;
};

export const ProjectsTable = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { filter: { common } } = useFilters();
  const isAnnual = props.isAnnual === 'annual' ? true : false;
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [dataFlattenedExcel, setDataFlattenedExcel] = useState([]);
  const isTabProject = useSelector<any, any>(state => state.tabValue);
  const reviewProjectsSelectedRows = useSelector<any, any>(state => state.reviewProjectsSelectedRows);

  const {
    selectable,
    withComments,
    reviewStyle,
    canCreateAccordance,
    fromReview,
    revision,
    fnPaginacion,
    initialPage,
    numberOfPages,
    numberOfProjects,
    projects,
    searchValue,
    itsFollowed = false,
  } = props;

  const currentCommonFilter = useRef(common);
  const currentSearchValue = useRef(searchValue);

  useEffect(() => {
    setIsAllSelected(reviewProjectsSelectedRows.length >= LIMIT_SELECTED_PROJECTS);
  }, []);

  useEffect(() => {
    if (currentCommonFilter.current !== common || currentSearchValue.current !== searchValue) {
      reviewProjectsSelectedRows.forEach(projectId => dispatch(setReviewSelectedProject(projectId)));
      sessionStorage.removeItem('selectedProjects');

      currentCommonFilter.current = common;
      currentSearchValue.current = searchValue;
    }
  }, [common, searchValue]);

  useEffect(() => {
    setIsAllSelected(reviewProjectsSelectedRows.length >= LIMIT_SELECTED_PROJECTS);
    setDataFlattenedExcel(getDataFromDownloadExcel(projects));
  }, [reviewProjectsSelectedRows]);

  const setInFollow = (projectId: number) => {
    props.setInFollow(projectId);
  };

  const sortTable = (sortValue: {}) => {
    props.sortTable(sortValue);
  };

  const { handlePaginationChange, handleSort, currentList, pagesCount, currentPage } = usePaginationSort({
    projects: projects,
    defaultSortCriteria: 'title',
    pageCount: rowsPerPage,
  });

  const headerItems = headersList(isAnnual, reviewStyle, canCreateAccordance);
  const totalColumns =
    headerItems.length + (selectable ? 1 : 0) + (withComments ? 1 : 0) + 2; /* two extra empty ths at the end */
  const hasRows = currentList && currentList.length > 0;

  const handleAllSelect = (e) => {
    e.stopPropagation();
    const currentListIds = currentList.map((project: Project) => project.id);
    if (!isAllSelected) {
      const remainingSelectableProjects = LIMIT_SELECTED_PROJECTS - reviewProjectsSelectedRows.length;
      const unselectedProjectIds = currentListIds.filter(id => !reviewProjectsSelectedRows.includes(id));
      const newSelectProjectsIds = unselectedProjectIds.slice(0, remainingSelectableProjects);

      newSelectProjectsIds.forEach(projectId => {
        dispatch(setReviewSelectedProject(projectId));
      });
    }
    else {
      reviewProjectsSelectedRows.forEach(projectId => dispatch(setReviewSelectedProject(projectId)));
      sessionStorage.removeItem('selectedProjects');
    }

    setIsAllSelected(!isAllSelected);
  };

  const selectSquareClass = () => (isAllSelected ? 'selected' : reviewProjectsSelectedRows.length > 0 ? 'partial' : '');

  return (
    <Fragment>
      <div className='projects-table-header'>
        <div className='projects-table-header-labels'>
          <span className='projects-table-header-title'>{numberOfProjects} {' '} {t('projects')}</span>
          {reviewProjectsSelectedRows.length > 0 && (<span>{reviewProjectsSelectedRows.length}{' '}{t(reviewProjectsSelectedRows.length > 1 ? 'selected_plural' : 'selected')}</span>)}
          {reviewProjectsSelectedRows.length === LIMIT_SELECTED_PROJECTS && (<span className='projects-table-header-alert'><i></i>{' '}{t('max_projects_selected')}</span>)}
        </div>
        <div className='projects-table-header-buttons'>
          <ReportsDownloadButton reviewProjectsSelectedRows={reviewProjectsSelectedRows} />
          <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={useDataNormalize(headers, selectedKeys, dataFlattenedExcel)} />
          {!isMobile() && (
            <div className="title-options-projects-all">
              <SearchInput itsFollowed={itsFollowed} />
            </div>
          )}
        </div>
      </div>
      <div className="table-idb">
        <>
          <table style={reviewStyle ? { borderCollapse: 'collapse' } : {}}>
            {isMobile() ? null : (
              <thead className={selectableThead(reviewStyle)} style={{ height: '88px', backgroundColor: '#F1F3F8' }}>
                <tr>
                  {(selectable && currentList && currentList.length > 0) && (
                    <th onClick={handleAllSelect}>
                      <span className={`select-square ${selectSquareClass()}`}></span>
                    </th>
                  )}
                  {headerItems.map(item => {
                    if (!hasRows) {
                      return <th key={item.label} id={item.label} />;
                    }
                    if (item.sortValue) {
                      return (
                        <th key={item.label} id={item.label} onClick={() => sortTable(item.sortValue || {})}>
                          <div className='text-head'>
                            <div>{t(item.label)}</div>
                            <div><i style={selectableI(reviewStyle)} className="sort-icon"></i></div>
                          </div>
                        </th>
                      );
                    } else {
                      return (
                        <th key="not-sorted" className="not-sorted">
                          {t(item.label)}
                        </th>
                      );
                    }
                  })}
                  {withComments && <th></th>}
                  <th></th>
                </tr>
              </thead>
            )}
            <tbody className={tBodyClassName(isMobile())}>
              {currentList && currentList.length > 0 ? (
                currentList.map((project: Project) => (
                  <ProjectsTableRow
                    key={project.id}
                    project={project}
                    setInFollow={setInFollow}
                    selectable={selectable}
                    withComments={withComments}
                    canCreateAccordance={canCreateAccordance}
                    fromReview={fromReview}
                    revision={revision}
                    disableSelection={isAllSelected}
                    enableActions={true}
                  />
                ))
              ) : (
                <tr>
                  <td className='p-5' colSpan={totalColumns}>
                    {searchValue ? (
                      <SearchTabErrorMessage search={searchValue} />
                    ) : (
                      <FiltersErrorMessage isTable={true} />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>

      </div>
      {isTabProject === 1 ?
        <div className="pagination-style pagination-right">
          <Pagination count={numberOfPages} page={initialPage} onChange={fnPaginacion} />
        </div> :
        <div className="pagination-style">
          <PaginationTable fnSetRows={setRowsPerPage} rows={rowsPerPage} numProyects={projects.length} />
          <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
        </div>
      }
    </Fragment>
  );
};
